import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { Empty, Spin, Table } from 'antd';
import { T } from 'react-translator-component';

import { CollapseToggle, useCollapsed } from './collapse';
import {
  connectorDistribution,
  feedIssues,
  formatDateTime,
  formatKwh,
  formatMoney,
  groupByOperator,
  hasFaultedConnector,
  SESSION_STATUS,
  sortByStartDesc,
  statusOf,
  sumBy,
  topStationsByEnergy,
} from './ocpiHelpers';
import type { Navigate } from './OcpiPage';
import StatusTag from './StatusTag';
import * as S from './styled';

import { PartnerFeedResult } from '../../../hooks/ocpi';
import { OcpiCdr, OcpiLocation, OcpiSession } from '../../../types/responses/Ocpi';
import { COLORS } from '../../../utils/theme/colors';

interface Props {
  locations: OcpiLocation[];
  sessions: OcpiSession[];
  cdrs: OcpiCdr[];
  partner?: PartnerFeedResult;
  nav: Navigate;
}

/** Cores de status do dashboard (utils/theme/colors). */
const GROUPS = [
  { key: 'available', label: 'Available', code: 'Available', color: COLORS.available },
  { key: 'inUse', label: 'In use', code: 'Charging', color: COLORS.busy },
  { key: 'faulted', label: 'Connector faulted', code: 'Faulted', color: COLORS.red },
  { key: 'unavailable', label: 'Connector unavailable', code: 'Unavailable', color: COLORS.stone },
] as const;

/** Recebendo: o resumo vem do cross e pode falhar ou não se aplicar sem afetar o lado que envia. */
const ReceivingNumbers = ({ partner }: { partner?: PartnerFeedResult }) => {
  if (!partner) return <Spin size="small" />;
  if (partner.status === 'error') return <S.Muted>{T('Could not load the stations received as a partner.')}</S.Muted>;
  if (partner.status === 'no-external-id') return <S.Muted>{T('This account does not receive stations as a partner.')}</S.Muted>;
  const inFeed = partner.stations.filter((s) => !feedIssues(s).length).length;
  return (
    <div className="numbers">
      <div>
        <div className="n">{partner.stations.length}</div>
        <div className="l">{T('stations')}</div>
      </div>
      <div>
        <div className="n">{groupByOperator(partner.stations).length}</div>
        <div className="l">{T('operators')}</div>
      </div>
      <div>
        <div className="n">{inFeed}</div>
        <div className="l">{T('go into the feed')}</div>
      </div>
      <div>
        <div className="n">{partner.stations.length - inFeed}</div>
        <div className="l">{T('discarded')}</div>
      </div>
    </div>
  );
};

const FlowCards = ({ locations, partner, nav }: Pick<Props, 'locations' | 'partner' | 'nav'>) => {
  const [collapsed, toggle] = useCollapsed('ocpi-overview-flow-collapsed');
  const received = partner?.status === 'ok' ? partner.stations.length : '—';

  return (
    <>
      <S.SectionHeader>
        <h3>{T('Sending and receiving')}</h3>
        <CollapseToggle collapsed={collapsed} onToggle={toggle} />
      </S.SectionHeader>
      {collapsed ? (
        <S.CollapsedSummary>
          <button type="button" onClick={() => nav.goTab('stations')}>
            <ArrowUpOutlined className="icon" />
            {T('Sending')} · <strong>{locations.length}</strong> {T('stations')}
          </button>
          <button type="button" onClick={() => nav.goTab('received')}>
            <ArrowDownOutlined className="icon" />
            {T('Receiving')} · <strong>{received}</strong> {T('stations')}
          </button>
        </S.CollapsedSummary>
      ) : (
  <S.Grid columns={2}>
    <S.FlowCard type="button" onClick={() => nav.goTab('stations')}>
      <div className="head">
        <span className="icon">
          <ArrowUpOutlined />
        </span>
        <div>
          <div className="title">{T('Sending')}</div>
          <div className="who">{T('Stations of this account that the partners see')}</div>
        </div>
      </div>
      <div className="numbers">
        <div>
          <div className="n">{locations.length}</div>
          <div className="l">{T('stations')}</div>
        </div>
        <div>
          <div className="n">{locations.reduce((n, l) => n + (l.connectors || []).length, 0)}</div>
          <div className="l">{T('connectors')}</div>
        </div>
        <div>
          <div className="n">{locations.filter(hasFaultedConnector).length}</div>
          <div className="l">{T('with a faulted connector')}</div>
        </div>
      </div>
      <span className="cta">{T('View sent stations')} →</span>
    </S.FlowCard>
    <S.FlowCard type="button" onClick={() => nav.goTab('received')}>
      <div className="head">
        <span className="icon">
          <ArrowDownOutlined />
        </span>
        <div>
          <div className="title">{T('Receiving')}</div>
          <div className="who">{T('Stations of other operators bound to this account in the cross')}</div>
        </div>
      </div>
      <ReceivingNumbers partner={partner} />
      <span className="cta">{T('View received stations')} →</span>
    </S.FlowCard>
  </S.Grid>
      )}
    </>
  );
};

const OverviewTab = ({ locations, sessions, cdrs, partner, nav }: Props) => {
  const [chargesCollapsed, toggleCharges] = useCollapsed('ocpi-overview-charges-collapsed');

  if (!locations.length) {
    return (
      <>
        <FlowCards locations={locations} partner={partner} nav={nav} />
        <S.Panel>
          <Empty description={T('No station is marked as OCPI compatible in this account yet.')} />
        </S.Panel>
      </>
    );
  }

  const active = sessions.filter((s) => s.status === 'ACTIVE').length;
  const dist = connectorDistribution(locations);
  const totalConnectors = GROUPS.reduce((total, group) => total + dist[group.key], 0) || 1;
  const top = topStationsByEnergy(sessions, locations);
  const topMax = top.length ? top[0].kwh : 1;
  const currency = cdrs.find((c) => c.currency)?.currency || 'BRL';
  const byUuid = new Map(locations.map((l) => [l.chargeBoxUuid, l]));

  return (
    <>
      <FlowCards locations={locations} partner={partner} nav={nav} />
      <S.SectionHeader>
        <h3>{T('Charges at the stations this account sends')}</h3>
        <CollapseToggle collapsed={chargesCollapsed} onToggle={toggleCharges} />
      </S.SectionHeader>
      {chargesCollapsed ? (
        <S.CollapsedSummary>
          <button type="button" onClick={() => nav.goTab('sessions')}>
            <strong>{sessions.length}</strong> {T('charges')} · <strong>{active}</strong> {T('ongoing')} ·{' '}
            <strong>{formatKwh(sumBy(sessions, (s) => s.kwh))}</strong>
          </button>
          <button type="button" onClick={() => nav.goTab('cdrs')}>
            <strong>{formatMoney(sumBy(cdrs, (c) => c.income), currency)}</strong> · {cdrs.length} CDRs
          </button>
        </S.CollapsedSummary>
      ) : (
        <>
      <S.Grid columns={3}>
        <S.Kpi type="button" onClick={() => nav.goTab('sessions')}>
          <span className="label">{T('Charges in progress')}</span>
          <span className="value">{active}</span>
          <span className="sub">{T('started in the period')}</span>
        </S.Kpi>
        <S.Kpi type="button" onClick={() => nav.goTab('sessions')}>
          <span className="label">{T('Energy in the period')}</span>
          <span className="value">{formatKwh(sumBy(sessions, (s) => s.kwh))}</span>
          <span className="sub">
            {sessions.length} {T('charges')}
          </span>
        </S.Kpi>
        <S.Kpi type="button" onClick={() => nav.goTab('cdrs')}>
          <span className="label">{T('Billed in the period')}</span>
          <span className="value">{formatMoney(sumBy(cdrs, (c) => c.income), currency)}</span>
          <span className="sub">{cdrs.length} CDRs</span>
        </S.Kpi>
      </S.Grid>

      <S.Grid columns={2}>
        <S.Panel style={{ marginBottom: 0 }}>
          <h2>{T('Connector status now')}</h2>
          <S.DistBar>
            {GROUPS.filter((g) => dist[g.key]).map((g) => (
              <span key={g.key} style={{ width: `${(dist[g.key] / totalConnectors) * 100}%`, background: g.color }} />
            ))}
          </S.DistBar>
          {GROUPS.map((g) => (
            <S.DistRow key={g.key}>
              <span className="swatch" style={{ background: g.color }} />
              <span className="n">{dist[g.key]}</span>
              <span className="label">{T(g.label)}</span>
              <S.Muted>{Math.round((dist[g.key] / totalConnectors) * 100)}%</S.Muted>
            </S.DistRow>
          ))}
        </S.Panel>

        <S.Panel style={{ marginBottom: 0 }}>
          <h2>{T('Stations with the most energy in the period')}</h2>
          {top.length === 0 && <S.Muted>{T('No charges in the period.')}</S.Muted>}
          {top.map(({ location, kwh }) => (
            <S.DistRow
              key={location.chargeBoxUuid}
              style={{ cursor: 'pointer' }}
              onClick={() => nav.open({ kind: 'station', uuid: location.chargeBoxUuid as string })}
            >
              <div className="label">
                <div>{location.chargeBoxId}</div>
                <S.Muted>
                  {location.street} · {location.city}
                </S.Muted>
              </div>
              <S.EnergyBar>
                <span style={{ width: `${(kwh / topMax) * 100}%` }} />
              </S.EnergyBar>
              <span style={{ whiteSpace: 'nowrap' }}>{formatKwh(kwh)}</span>
            </S.DistRow>
          ))}
        </S.Panel>
      </S.Grid>

      <S.Panel style={{ marginTop: 16 }}>
        <h2>{T('Recent charges')}</h2>
        <Table
          size="small"
          rowKey="transactionPk"
          pagination={false}
          dataSource={sortByStartDesc(sessions).slice(0, 6)}
          onRow={(record) => ({ onClick: () => nav.open({ kind: 'session', pk: record.transactionPk }) })}
          locale={{ emptyText: T('No charges in the period.') }}
          columns={[
            { title: T('Charge'), dataIndex: 'transactionPk', render: (pk) => <S.Mono>#{pk}</S.Mono> },
            { title: T('Status'), dataIndex: 'status', render: (s) => <StatusTag {...statusOf(SESSION_STATUS, s)} /> },
            {
              title: T('Station'),
              render: (_, r) => `${byUuid.get(r.chargeBoxUuid)?.chargeBoxId || r.chargeBoxUuid || '—'} · ${r.connectorId ?? '—'}`,
            },
            { title: T('Start'), dataIndex: 'startTimestamp', render: formatDateTime },
            { title: T('Energy'), dataIndex: 'kwh', align: 'right', render: formatKwh },
            { title: T('Amount'), align: 'right', render: (_, r) => formatMoney(r.income, r.currency) },
          ]}
        />
      </S.Panel>
        </>
      )}
    </>
  );
};

export default OverviewTab;
